/**
 * Wizard-Form - Main orchestrator component
 *
 * Manages multi-step form flow, validation, and submission.
 *
 * @example
 * <wizard-form hubspot-portal="12345" hubspot-form="abc-123">
 *   <wf-step data-step="1">...</wf-step>
 *   <wf-step data-step="2">...</wf-step>
 *   <wf-success>Thank you!</wf-success>
 * </wizard-form>
 */

import { LitElement, html, nothing, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { wizardFormStyles } from './wizard-form.styles.js';
import { FormStateController } from '../controllers/FormStateController.js';
import { KeyboardController } from '../controllers/KeyboardController.js';
import type { WfStep } from './wf-step.js';
import type { Theme, HubSpotSubmissionData } from '../types.js';
// Import wf-badge for button shortcuts
import './wf-badge.js';

// Event detail types
export interface StepChangeDetail {
  from: number;
  to: number;
  direction: 'forward' | 'backward';
  submitted?: boolean;
  adapter?: 'hubspot';
}

export interface FieldChangeDetail {
  name: string;
  value: unknown;
  formData: Record<string, unknown>;
}

export interface SubmitDetail {
  formData: Record<string, unknown>;
}

export interface SuccessDetail {
  formData: Record<string, unknown>;
  response?: unknown;
}

export interface ErrorDetail {
  error: string;
  formData?: Record<string, unknown>;
}

@customElement('wizard-form')
export class WizardForm extends LitElement {
  static styles = wizardFormStyles;

  // ============================================
  // Properties
  // ============================================

  /**
   * HubSpot portal ID
   */
  @property({ type: String, attribute: 'hubspot-portal' })
  hubspotPortal = '';

  /**
   * HubSpot form ID
   */
  @property({ type: String, attribute: 'hubspot-form' })
  hubspotForm = '';

  /**
   * Color theme
   */
  @property({ type: String, reflect: true })
  theme: Theme = 'light';

  /**
   * Mock submission mode (for testing)
   */
  @property({ type: Boolean })
  mock = false;

  /**
   * Show progress bar
   */
  @property({ type: Boolean, attribute: 'show-progress' })
  showProgress = false;

  /**
   * Auto-advance after single-select option
   */
  @property({ type: Boolean, attribute: 'auto-advance' })
  autoAdvance = true;

  /**
   * Hide the back button in navigation
   */
  @property({ type: Boolean, attribute: 'hide-back' })
  hideBack = true;

  /**
   * Transform form data before submission.
   * Applied before HubSpot submission and passed to wf:success event.
   */
  @property({ attribute: false })
  serialize?: (data: Record<string, unknown>) => Record<string, unknown>;

  /**
   * Submit partial form data to HubSpot after each step change.
   * When enabled, form data is submitted after each step navigation (blocking).
   * Only fields from completed steps are included in partial submissions.
   */
  @property({ type: Boolean, attribute: 'submit-on-step' })
  submitOnStep = false;

  // ============================================
  // State
  // ============================================

  @state()
  private _steps: WfStep[] = [];

  @state()
  private _currentStep = 1;

  @state()
  private _totalSteps = 0;

  @state()
  private _formData: Record<string, unknown> = {};

  @state()
  private _submitting = false;

  /**
   * Whether form has been successfully submitted
   * Reflected to attribute for CSS styling of slotted elements
   */
  @property({ type: Boolean, reflect: true })
  submitted = false;

  @state()
  private _error = '';

  /**
   * Tracks if user is typing in "Others" input (for dynamic Continue button visibility)
   */
  @state()
  private _otherInputActive = false;

  /**
   * Tracks if partial submission is in progress (prevents duplicate submissions)
   */
  private _partialSubmitting = false;

  // ============================================
  // Controllers
  // ============================================

  private _stateController = new FormStateController(this);

  constructor() {
    super();

    // Initialize keyboard controller (attaches to host automatically)
    new KeyboardController(this, {
      onEnter: () => this._handleEnter(),
      onEscape: () => this._handleEscape(),
    });
  }

  // ============================================
  // Lifecycle
  // ============================================

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('wf-change', this._handleFieldChange as EventListener);
    this.addEventListener('wf-option-select', this._handleOptionSelect as EventListener);
    // Listen for composable navigation events
    this.addEventListener('wf:nav-next', this._handleNavNext);
    this.addEventListener('wf:nav-back', this._handleNavBack);
    this.addEventListener('wf:nav-state-request', this._handleNavStateRequest);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('wf-change', this._handleFieldChange as EventListener);
    this.removeEventListener('wf-option-select', this._handleOptionSelect as EventListener);
    this.removeEventListener('wf:nav-next', this._handleNavNext);
    this.removeEventListener('wf:nav-back', this._handleNavBack);
    this.removeEventListener('wf:nav-state-request', this._handleNavStateRequest);
  }

  firstUpdated(): void {
    this._discoverSteps();
    this._showCurrentStep();

    // Broadcast initial nav state for composable buttons
    this._broadcastNavState();
  }

  // ============================================
  // Rendering
  // ============================================

  render() {
    // When submitted, hide steps (via CSS) and show success slot
    if (this.submitted) {
      // Deactivate all steps
      this._steps.forEach(step => {
        step.active = false;
      });

      return html`
        <div class="wf-container" data-testid="wf-container">
          <slot name="success">
            <div class="wf-success-screen">
              <h2>Thank you!</h2>
              <p>Your submission has been received.</p>
            </div>
          </slot>
        </div>
      `;
    }

    return html`
      <div class="wf-container" data-testid="wf-container">
        ${this._error
          ? html`<div class="wf-form-error" role="alert" data-testid="wf-form-error">${this._error}</div>`
          : nothing}

        <slot @slotchange="${this._handleSlotChange}"></slot>
      </div>
    `;
  }

  /**
   * Handle slot changes to re-discover steps when DOM changes
   */
  private _handleSlotChange(): void {
    this._discoverSteps();
  }

  private _renderProgress() {
    const segments = [];
    for (let i = 1; i <= this._totalSteps; i++) {
      const completed = i < this._currentStep;
      const active = i === this._currentStep;
      segments.push(html`
        <div
          class="wf-progress-segment ${completed ? 'completed' : ''} ${active ? 'active' : ''}"
        ></div>
      `);
    }
    return html`<div class="wf-progress">${segments}</div>`;
  }

  /**
   * Centralized navigation is no longer rendered.
   * All navigation is now fully composable via <wf-next-btn> and <wf-back-btn>.
   * Keyboard shortcuts (Enter/Esc) still work regardless of button presence.
   */
  private _renderNavigation() {
    // Navigation is fully composable - nothing to render centrally
    return nothing;
  }

  // ============================================
  // Step Discovery
  // ============================================

  private _discoverSteps(): void {
    // Shadow DOM: Get slotted wf-step elements
    const slot = this.shadowRoot?.querySelector('slot:not([name])') as HTMLSlotElement | null;
    if (slot) {
      const assignedElements = slot.assignedElements({ flatten: true });
      this._steps = assignedElements.filter(
        (el): el is WfStep => el.tagName.toLowerCase() === 'wf-step'
      );
    } else {
      // Fallback: query light DOM (for initial render before slot is ready)
      this._steps = Array.from(this.querySelectorAll(':scope > wf-step')) as WfStep[];
    }

    // Auto-assign step numbers based on DOM order (1-indexed)
    // This makes data-step attribute optional
    this._steps.forEach((step, index) => {
      step.step = index + 1;
    });

    this._totalSteps = this._steps.length;
    this._stateController.setTotalSteps(this._totalSteps);

    // Dispatch event on document so wf-progress can always catch it
    // (even if wf-progress is positioned before wizard-form in DOM)
    document.dispatchEvent(
      new CustomEvent('wf:steps-discovered', {
        detail: {
          wizard: this,
          wizardId: this.id,
          totalSteps: this._totalSteps,
          currentStep: this._currentStep,
        },
      })
    );
  }

  private _showCurrentStep(): void {
    this._steps.forEach((step, index) => {
      const stepNumber = index + 1;
      if (stepNumber === this._currentStep) {
        step.show('forward');
      } else {
        step.active = false;
      }
    });
  }

  /**
   * Get step element by step number (1-indexed)
   */
  private _getStepByNumber(stepNumber: number): WfStep | undefined {
    const index = stepNumber - 1;
    return this._steps[index];
  }

  // ============================================
  // Navigation
  // ============================================

  /**
   * Check if the current step requires manual navigation (Continue button).
   * Returns true if step contains typing inputs or multi-select options.
   * Returns false if step only has single-select options (auto-advance handles it).
   */
  private _stepRequiresManualNav(): boolean {
    // If auto-advance is disabled, always show nav
    if (!this.autoAdvance) {
      return true;
    }

    const currentStep = this._getStepByNumber(this._currentStep);
    if (!currentStep) {
      return true;
    }

    // Check for typing controls
    const typingControls = ['wf-input', 'wf-email', 'wf-textarea', 'wf-number'];
    for (const selector of typingControls) {
      if (currentStep.querySelector(selector)) {
        return true;
      }
    }

    // Check for multi-select options
    const multiSelectOptions = currentStep.querySelector('wf-options[multi]');
    if (multiSelectOptions) {
      return true;
    }

    // For "allow-other" options: only show nav if user is typing in "Other" input
    // Otherwise, auto-advance will handle regular option selection
    const allowOtherOptions = currentStep.querySelector('wf-options[allow-other]');
    if (allowOtherOptions && this._otherInputActive) {
      return true;
    }

    // Step only has single-select options (or allow-other without text) - auto-advance handles it
    return false;
  }

  /**
   * Check if the current step has composable navigation buttons at the step level.
   * Excludes buttons inside wf-other (those are for inline "Others" input only).
   * When present, auto-advance is disabled for the step.
   */
  private _stepHasComposableNav(): boolean {
    const currentStep = this._getStepByNumber(this._currentStep);
    if (!currentStep) {return false;}
    // Find nav buttons that are NOT inside wf-other
    const navButtons = currentStep.querySelectorAll('wf-next-btn, wf-back-btn');
    for (const btn of navButtons) {
      // Check if this button is inside a wf-other element
      if (!btn.closest('wf-other')) {
        return true;
      }
    }
    return false;
  }

  /**
   * Broadcast navigation state to composable buttons.
   * Called on step changes and submit state changes.
   */
  private _broadcastNavState(): void {
    this.dispatchEvent(
      new CustomEvent('wf:nav-state', {
        detail: {
          currentStep: this._currentStep,
          totalSteps: this._totalSteps,
          isFirstStep: this._currentStep === 1,
          isLastStep: this._currentStep === this._totalSteps,
          isSubmitting: this._submitting,
        },
        bubbles: false,
      })
    );
  }

  private async _goNext(): Promise<void> {
    if (this._submitting) {return;}

    // Validate current step
    const currentStep = this._getStepByNumber(this._currentStep);
    if (currentStep) {
      const isValid = await currentStep.validate();
      if (!isValid) {
        return;
      }
    }

    if (this._currentStep >= this._totalSteps) {
      // Submit form
      await this._submit();
    } else {
      // Go to next step
      this._navigateToStep(this._currentStep + 1, 'forward');
    }
  }

  private _goBack(): void {
    if (this._submitting) {return;}
    if (this._currentStep <= 1) {return;}

    this._navigateToStep(this._currentStep - 1, 'backward');
  }

  private async _navigateToStep(
    targetStep: number,
    direction: 'forward' | 'backward'
  ): Promise<void> {
    if (targetStep < 1 || targetStep > this._totalSteps) {return;}

    const targetStepEl = this._getStepByNumber(targetStep);
    if (!targetStepEl) {return;}

    // Check skip condition
    if (targetStepEl.shouldSkip(this._formData)) {
      const nextTarget = direction === 'forward' ? targetStep + 1 : targetStep - 1;
      if (nextTarget >= 1 && nextTarget <= this._totalSteps) {
        return this._navigateToStep(nextTarget, direction);
      }
      return;
    }

    const previousStep = this._currentStep;

    // Partial submission (blocking) - submit before navigation
    let submitted = false;
    let adapter: 'hubspot' | undefined;

    // Only do partial submission if:
    // 1. submitOnStep is enabled
    // 2. HubSpot config exists
    // 3. Going forward (not backward)
    // 4. Not already submitting a partial
    if (
      this.submitOnStep &&
      this.hubspotPortal &&
      this.hubspotForm &&
      direction === 'forward' &&
      !this._partialSubmitting
    ) {
      try {
        this._partialSubmitting = true;
        await this._submitPartialToHubSpot();
        submitted = true;
        adapter = 'hubspot';
      } catch (error) {
        // Block navigation on failure
        console.error('[wizard-form] Partial submission failed:', error);
        this.dispatchEvent(
          new CustomEvent<ErrorDetail>('wf:error', {
            detail: {
              error: error instanceof Error ? error.message : 'Partial submission failed',
              formData: this._getFieldsUpToStep(this._currentStep),
            },
            bubbles: true,
            composed: true,
          })
        );
        return; // Don't proceed to next step
      } finally {
        this._partialSubmitting = false;
      }
    }

    // Hide current step
    const currentStepEl = this._getStepByNumber(this._currentStep);
    if (currentStepEl) {
      await currentStepEl.hide();
    }

    // Update current step
    this._currentStep = targetStep;
    this._stateController.goToStep(targetStep);

    // Reset "Other" input state for new step
    this._otherInputActive = false;

    // Broadcast nav state for composable buttons
    this._broadcastNavState();

    // Show new step
    targetStepEl.show(direction);

    // Dispatch step change event with submission metadata
    this.dispatchEvent(
      new CustomEvent<StepChangeDetail>('wf:step-change', {
        detail: {
          from: previousStep,
          to: targetStep,
          direction,
          submitted,
          adapter,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  // ============================================
  // Event Handlers
  // ============================================

  private _handleFieldChange = (e: CustomEvent): void => {
    const { name, value, extraFields } = e.detail;
    this._formData[name] = value;
    this._stateController.setValue(name, value);

    // Handle dual-field submission (e.g., "Others" with custom text from wf-options)
    if (extraFields) {
      Object.entries(extraFields).forEach(([fieldName, fieldValue]) => {
        this._formData[fieldName] = fieldValue;
        this._stateController.setValue(fieldName, fieldValue as string);
      });

      // Track if "Other" input has text (for dynamic Continue button visibility)
      const hasOtherText = Object.values(extraFields).some(v => v && String(v).trim());
      this._otherInputActive = hasOtherText;
    } else {
      // If no extraFields, user selected a regular option - reset Other state
      this._otherInputActive = false;
    }

    // Clear form error on field change
    this._error = '';

    this.dispatchEvent(
      new CustomEvent<FieldChangeDetail>('wf:field-change', {
        detail: {
          name,
          value,
          formData: { ...this._formData },
        },
        bubbles: true,
        composed: true,
      })
    );
  };

  private _handleOptionSelect = (e: CustomEvent): void => {
    // Skip auto-advance if step has composable navigation buttons
    if (this._stepHasComposableNav()) {
      return;
    }

    // Check if this is a single-select option and auto-advance is enabled
    if (this.autoAdvance) {
      const target = e.target as Element;
      const optionsParent = target.closest('wf-options');
      if (optionsParent && !optionsParent.hasAttribute('multi')) {
        // Delay to show selection animation
        setTimeout(() => {
          this._goNext();
        }, 300);
      }
    }
  };

  private async _handleEnter(): Promise<void> {
    await this._goNext();
  }

  private _handleEscape(): void {
    this._goBack();
  }

  /**
   * Handle composable wf-next-btn click
   */
  private _handleNavNext = async (e: Event): Promise<void> => {
    e.stopPropagation();
    await this._goNext();
  };

  /**
   * Handle composable wf-back-btn click
   */
  private _handleNavBack = (e: Event): void => {
    e.stopPropagation();
    this._goBack();
  };

  /**
   * Handle request for navigation state (from composable buttons on connect)
   */
  private _handleNavStateRequest = (): void => {
    this._broadcastNavState();
  };

  // ============================================
  // Submission
  // ============================================

  private async _submit(): Promise<void> {
    if (this._submitting) {return;}

    const rawData: Record<string, unknown> = { ...this._formData };

    // Dispatch submit event with RAW data (cancelable)
    // This allows init() or user code to apply their own transformations
    const submitEvent = new CustomEvent<SubmitDetail>('wf:submit', {
      detail: { formData: rawData },
      bubbles: true,
      composed: true,
      cancelable: true,
    });

    this.dispatchEvent(submitEvent);

    if (submitEvent.defaultPrevented) {
      return;
    }

    this._submitting = true;
    this._error = '';

    // Broadcast nav state to update composable button loading state
    this._broadcastNavState();

    // Apply serialize transform if provided (for built-in HubSpot submission)
    let submitData = rawData;
    if (this.serialize) {
      submitData = this.serialize(rawData);
    }

    try {
      let response: unknown;

      if (this.mock) {
        // Mock submission for testing
        await new Promise((resolve) => setTimeout(resolve, 1000));
        response = { success: true };
      } else if (this.hubspotPortal && this.hubspotForm) {
        // Submit to HubSpot with serialized data
        response = await this._submitToHubSpot(submitData);
      } else {
        // No submission target configured
        console.warn('[WizardForm] No submission target configured. Use hubspot-portal/hubspot-form or mock.');
        response = { success: true };
      }

      this.submitted = true;

      // wf:success receives serialized data (same as what went to HubSpot)
      this.dispatchEvent(
        new CustomEvent<SuccessDetail>('wf:success', {
          detail: {
            formData: submitData,
            response,
          },
          bubbles: true,
          composed: true,
        })
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Submission failed';
      this._error = errorMessage;

      this.dispatchEvent(
        new CustomEvent<ErrorDetail>('wf:error', {
          detail: {
            error: errorMessage,
            formData: submitData,
          },
          bubbles: true,
          composed: true,
        })
      );
    } finally {
      this._submitting = false;
      // Broadcast nav state to update composable button loading state
      this._broadcastNavState();
    }
  }

  private async _submitToHubSpot(formData: Record<string, unknown>): Promise<unknown> {
    const endpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${this.hubspotPortal}/${this.hubspotForm}`;

    // Convert form data to HubSpot format
    const fields = Object.entries(formData).map(([name, value]) => ({
      name,
      value: Array.isArray(value) ? value.join(';') : String(value ?? ''),
    }));

    const data: HubSpotSubmissionData = {
      fields,
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HubSpot submission failed: ${response.status}`);
    }

    return response.json();
  }

  // ============================================
  // Partial Submission
  // ============================================

  /**
   * Get form fields from steps up to and including the specified step number.
   * Used for partial submission to only include completed step data.
   */
  private _getFieldsUpToStep(stepNumber: number): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    // Get all steps up to and including the specified step
    for (let i = 0; i < stepNumber && i < this._steps.length; i++) {
      const step = this._steps[i];
      // Find all field components in this step
      const fields = step.querySelectorAll('[name]') as NodeListOf<HTMLElement & { name: string }>;
      fields.forEach((field) => {
        if (field.name && this._formData[field.name] !== undefined) {
          result[field.name] = this._formData[field.name];
        }
      });
    }

    return result;
  }

  /**
   * Submit partial form data to HubSpot.
   * Only includes fields from steps up to and including the current step.
   * Filters out empty/blank values to avoid submitting unfilled fields.
   */
  private async _submitPartialToHubSpot(): Promise<void> {
    // Only include fields from steps up to and including the current step
    const partialData = this._getFieldsUpToStep(this._currentStep);

    // Apply serialize transform if provided
    let submitData = { ...partialData };
    if (this.serialize) {
      submitData = this.serialize(submitData);
    }

    // Filter out empty/blank values from partial submission
    const filteredData = Object.fromEntries(
      Object.entries(submitData).filter(([, value]) => {
        // Keep the value if it's not null, undefined, or empty string
        if (value === null || value === undefined || value === '') {
          return false;
        }
        // For arrays, keep if not empty
        if (Array.isArray(value) && value.length === 0) {
          return false;
        }
        return true;
      })
    );

    await this._submitToHubSpot(filteredData);
  }

  // ============================================
  // Public API
  // ============================================

  /**
   * Get current form data
   */
  get formData(): Record<string, unknown> {
    return { ...this._formData };
  }

  /**
   * Get current step number
   */
  get currentStep(): number {
    return this._currentStep;
  }

  /**
   * Get total number of steps
   */
  get totalSteps(): number {
    return this._totalSteps;
  }

  /**
   * Check if form is submitting
   */
  get isSubmitting(): boolean {
    return this._submitting;
  }

  /**
   * Check if form was submitted
   */
  get isSubmitted(): boolean {
    return this.submitted;
  }

  /**
   * Go to a specific step
   */
  goToStep(step: number): void {
    const direction = step > this._currentStep ? 'forward' : 'backward';
    this._navigateToStep(step, direction);
  }

  /**
   * Go to next step
   */
  next(): Promise<void> {
    return this._goNext();
  }

  /**
   * Go to previous step
   */
  back(): void {
    this._goBack();
  }

  /**
   * Submit the form
   */
  submit(): Promise<void> {
    return this._submit();
  }

  /**
   * Reset the form
   */
  reset(): void {
    this._formData = {};
    this._currentStep = 1;
    this._submitting = false;
    this.submitted = false;
    this._error = '';
    this._stateController.reset();
    this._showCurrentStep();

    // Reset all field components
    this._steps.forEach((step) => {
      const fields = step.querySelectorAll('wf-options, wf-field, wf-email');
      fields.forEach((field) => {
        if ('reset' in field && typeof (field as { reset: () => void }).reset === 'function') {
          (field as { reset: () => void }).reset();
        }
      });
    });
  }

  /**
   * Set form data
   */
  setFormData(data: Record<string, unknown>): void {
    this._formData = { ...this._formData, ...data };
    Object.entries(data).forEach(([name, value]) => {
      this._stateController.setValue(name, value);
    });
  }
}

// Success screen component
@customElement('wf-success')
export class WfSuccess extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    :host([hidden]) {
      display: none;
    }
  `;

  render() {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wizard-form': WizardForm;
    'wf-success': WfSuccess;
  }
}
